import React from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Divider, TextField, Typography, Checkbox, FormControlLabel} from '@mui/material';
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import {EndScreen} from "crqlar-react-common/dist/models/forms/form";

interface EndScreenInputProps {
  question: EndScreen;
  onQuestionUpdate: (question: EndScreen) => void;
  isSelected?: boolean;
  onSelect?: () => void;
}

const EndScreenInput: React.FC<EndScreenInputProps> = ({
  question,
  onQuestionUpdate,
  isSelected = false,
  onSelect
}) => {
  const {t} = useTranslation();

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      title: e.target.value
    });
  };

  const handleDescriptionChange = (value?: string) => {
    onQuestionUpdate({
      ...question,
      description: value || ''
    });
  };

  const handleButtonTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      buttonText: e.target.value
    });
  };

  const handleRedirectUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      redirectUrl: e.target.value
    });
  };

  const handleCollectEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      collectEmail: e.target.checked
    });
  };

  const handleEmailPlaceholderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      emailPlaceholder: e.target.value
    });
  };

  return (
    <Box
      onClick={onSelect}
      sx={{
        p: 4,
        mb: 2,
        borderRadius: 1,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.light',
          backgroundColor: 'action.hover'
        }
      }}
    >
      <Divider sx={{mb: 3}} />

      {/* End Section Header */}
      <Typography variant="overline" color="primary" sx={{mb: 2, display: 'block', fontWeight: 'bold'}}>
        {t('End Screen')}
      </Typography>

      {isSelected ? (
        <>
          <TextField
            fullWidth
            label={t('Thank You Title')}
            value={question.title}
            onChange={handleTitleChange}
            variant="standard"
            onClick={(e) => e.stopPropagation()}
            placeholder={t('Thank you for your submission!')}
            sx={{mb: 2}}
          />

          <Box sx={{mb: 3}} onClick={(e) => e.stopPropagation()}>
            <MdEditor
              value={question.description || ''}
              onChange={handleDescriptionChange}
              placeholder={t('Closing Message (optional)')}
              className={'mini-mdx'}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={question.collectEmail || false}
                onChange={handleCollectEmailChange}
                onClick={(e) => e.stopPropagation()}
              />
            }
            label={t('Collect email address')}
            sx={{mb: 2}}
          />

          {question.collectEmail && (
            <TextField
              fullWidth
              label={t('Email Placeholder')}
              value={question.emailPlaceholder || ''}
              onChange={handleEmailPlaceholderChange}
              variant="outlined"
              size="small"
              sx={{mb: 3}}
              onClick={(e) => e.stopPropagation()}
              placeholder={t('Enter your email')}
            />
          )}

          <TextField
            fullWidth
            label={t('Button Text')}
            value={question.buttonText || ''}
            onChange={handleButtonTextChange}
            variant="outlined"
            size="small"
            sx={{mb: 2}}
            onClick={(e) => e.stopPropagation()}
            placeholder={t('Submit')}
          />

          <TextField
            fullWidth
            label={t('Redirect URL (optional)')}
            value={question.redirectUrl || ''}
            onChange={handleRedirectUrlChange}
            variant="outlined"
            size="small"
            onClick={(e) => e.stopPropagation()}
            placeholder="https://example.com/thank-you"
            helperText={t('Where to redirect users after form submission')}
          />
        </>
      ) : (
        <>
          <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
            {question.title}
          </Typography>
          {question.description && (
            <Box sx={{mb: 3, textAlign: 'center'}}>
              <MdRenderer>
                {question.description}
              </MdRenderer>
            </Box>
          )}

          {question.collectEmail && (
            <TextField
              fullWidth
              disabled
              placeholder={question.emailPlaceholder || t('Enter your email')}
              variant="outlined"
              size="small"
              sx={{mb: 2, maxWidth: 400, mx: 'auto', display: 'block'}}
            />
          )}

          <Box sx={{textAlign: 'center', mt: 2}}>
            <Typography
              variant="button"
              sx={{
                px: 3,
                py: 1,
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: 1,
                display: 'inline-block'
              }}
            >
              {question.buttonText || t('Submit')}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EndScreenInput;
